import axiosInstance from "./auth";
import { AxiosProgressEvent } from "axios";
import {
  Attribute,
  AttributePayload,
  AttributeSet,
  AttributeSetPayload,
  Category,
  CategoryInput,
  Contact,
  CreateCouponPayload,
  FetchUsersResponse,
  GoldPriceResponse,
  InventorySalesReportParams,
  LoginPayload,
  NewUserData,
  Order,
  Page,
  PageFormData,
  PaginatedOrdersResponse,
  pinCode,
  Product,
  Tax,
  UpdateCouponPayload,
  Variant,
} from "../interface";

interface ApiResponse<T> {
  data: T;
}

interface PagesResponse {
  success: boolean;
  count: number;
  pages: Page[];
}

interface PageResponse {
  success: boolean;
  message: string;
  page: Page;
}

interface StatusResponse {
  success: boolean;
  message: string;
  isActive: boolean;
}

export interface ContactInfo {
  _id?: string;
  officeNo: string;
  floor: string;
  building: string;
  landmark: string;
  street: string;
  area: string;
  gstin: string;
  pincode: string;
  email: string;
  phone: string;
  whatshappNo: string;
  mapLink: string;
  mapIframe?: string;
  fullAddress?: string;
  createdAt?: string;
  updatedAt?: string;
  isDefault?: boolean;
}

export interface ContactInfoResponse {
  success: boolean;
  contactInfo?: ContactInfo; // Make this optional since backend might return direct object
}

export const login = (data: LoginPayload) => {
  return axiosInstance.post("/admin/admin-login", data);
};

// export const getAllProducts = async (): Promise<Product[]> => {
//   const response = await axiosInstance.get<{ products: Product[] }>(
//     "/products/Get/Allproduct"
//   );
//   return response.data.products;
// };

export const getAllProducts = async (
  page: number,
  limit: number,
): Promise<{
  products: Product[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}> => {
  const response = await axiosInstance.get(
    `/products/Get/getAllAdminProducts?page=${page}&limit=${limit}`,
  );
  return response.data;
};

export const getCurrentUser = () => axiosInstance.get("/get/getCurrentUser");

export interface GoldPrices {
  "18K": number;
  "22K": number;
  "24K": number;
}

interface ImportResponse {
  success: boolean;
  message: string;
  errors?: Array<{
    row: number;
    error: string;
  }>;
}

interface GetProductResponse {
  success: boolean;
  images: string;
  product: {
    video: any;
    taxable: any;
    isFeatured: boolean;
    isNewArrival: boolean;
    isTaxable: any;
    images: string;
    _id: string;
    name: string;
    sku?: string;
    category: string | { _id: string; name?: string };
    currentBasePrice: number;
    basePrice: number;
    variants: Variant[];
    isSimpleProduct: boolean;
    attributeSet?:
      | {
          _id: string;
          name: string;
          attributes: Attribute[];
        }
      | string; // Could be object or string ID
    mainImages?: string[];
    // Add other product fields as needed
  };
  allVariants?: Array<{
    video: string;
    _id: string;
    sku: string;
    price: number;
    quantity: number;
    images: string[];
    attributes: Array<{
      _id: string;
      label: string;
      value: string | number;
      valueType: string;
    }>;
  }>;
  message?: string;
}

interface AddToCartItem {
  product: string; // product ID
  variantId: string;
  quantity: number;
}

interface AddToCartPayload {
  products: AddToCartItem[];
  userId?: string; // optional if auth middleware sets it
}

interface AddToCartResponse {
  success: boolean;
  message: string;
  // cart: any; // Replace with actual Cart type if available
  itemCount: number;
  validationErrors?: Array<any>;
  cart?: {
    _id: string;
  };
}

export const updateGoldPrice = (data: {
  prices: { [key: string]: number };
}) => {
  return axiosInstance.put("/products/update-gold-price", data);
};

export const addProduct = async (
  productData: FormData,
): Promise<{ success: boolean; product: Product; message?: string }> => {
  try {
    const response = await axiosInstance.post(
      "/products/addproduct",
      productData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (!response.data) {
      throw new Error("No response received from server");
    }

    return {
      success: response.data.success || false,
      product: response.data.product,
      message: response.data.message,
    };
  } catch (error: any) {
    console.error("API Error:", error);
    throw new Error(
      error.response?.data?.error ||
        error.message ||
        "Failed to create product",
    );
  }
};

// Get a product by its ID
export const getProductById = async (
  id: string,
): Promise<GetProductResponse> => {
  const response = await axiosInstance.get<GetProductResponse>(
    `/products/${id}`,
  );
  return response.data; // ✅ No TS error now
};

// Update a product by its ID //Notworking
// export const updateProductById = async (
//   productData: FormData
// ): Promise<Product> => {
//   // Extract productId from FormData
//   const productId = productData.get("productId");

//   if (!productId || typeof productId !== "string") {
//     throw new Error("Product ID is required for update");
//   }

//   // Use PUT or POST based on backend (adjust if needed)
//   const response = await axiosInstance.post(
//     `/products/updateProductById`, // pass productId in URL if backend expects it here
//     productData,
//     {
//       headers: {
//         "Content-Type": "multipart/form-data",
//       },
//     }
//   );

//   return response.data.product;
// };
export const updateProductById = async (
  productData: FormData,
): Promise<{ success: boolean; product?: any; error?: string }> => {
  try {
    const productId = productData.get("productId");

    if (!productId || typeof productId !== "string") {
      throw new Error("Product ID is required for update");
    }

    const response = await axiosInstance.post(
      `/products/updateProductById`,
      productData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    // Ensure consistent response structure
    return {
      success: true,
      product: response.data.product || response.data,
    };
  } catch (error: any) {
    console.error("Update API Error:", error);
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to update product",
    };
  }
};

export const updateProductMedia = async (
  productId: string,
  mediaData: {
    deletedProductImages?: string | string[] | "true";
    deleteProductVideo?: "true";
    deletedVariantImages?: string[];
    deletedVariantVideos?: string | number | number[];
    existingProductImages?: string | string[];
    existingVariantImages?: string[];
    existingProductVideo?: string;
    existingVariantVideos?: string[];
  },
): Promise<{ success: boolean; product?: any; error?: string }> => {
  try {
    if (!productId) throw new Error("Product ID is required for media update");

    const response = await axiosInstance.put(
      `/products/${productId}/media`,
      mediaData, // Send as JSON object
      {
        headers: {
          "Content-Type": "application/json", // Change to JSON
        },
      },
    );

    return {
      success: true,
      product: response.data.product || response.data,
    };
  } catch (error: any) {
    console.error("Update Product Media API Error:", error);
    return {
      success: false,
      error:
        error.response?.data?.error ||
        error.message ||
        "Failed to update product media",
    };
  }
};

// Delete product by ID
export const deleteProductById = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/products/delete/${id}`);
};

export const fetchUsers = async (
  page: number = 1,
  limit: number = 10,
  search: string = "",
  dateFilter: string = "all"
): Promise<FetchUsersResponse> => {

  const response = await axiosInstance.get<FetchUsersResponse>(
    `/get/fetchUsers?page=${page}&limit=${limit}&search=${encodeURIComponent(
      search
    )}&dateFilter=${encodeURIComponent(dateFilter)}`
  );

  return response.data;
};

export const addUserByAdmin = async (userData: NewUserData) => {
  const response = await axiosInstance.post("/addUser", userData);
  return response.data;
};

export const editUserByAdmin = async (
  userId: string,
  updateData: {
    name?: string;
    email?: string;
    phoneNumber?:string;
    status?: string;
  },
) => {
  const response = await axiosInstance.put(
    `/Up/editUserByAdmin/${userId}`,
    updateData,
  );
  return response.data.user;
};

// Delete user by ID (Admin only)
export const deleteUserById = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/deleteUser/${id}`);
};

export const fetchOrders = async (
  page: number = 1,
  limit: number = 10,
): Promise<PaginatedOrdersResponse> => {
  const response = await axiosInstance.get<PaginatedOrdersResponse>(
    `/order/getAll`, // Changed to match your actual endpoint
    {
      params: {
        // Better to use params object for query parameters
        page,
        limit,
      },
    },
  );
  return response.data;
};

// Add this to your API file (apis.ts or wherever you have your other API functions)

export const fetchOrder = async (orderId: string) => {
  const response = await axiosInstance.get(`/order/${orderId}`);
  return response.data;
};

export const getOrderById = async (orderId: string): Promise<Order> => {
  const response = await axiosInstance.get<{ order: Order }>(
    `/order/${orderId}`,
  );
  return response.data.order;
};

export const updateItemStatus = async (
  itemId: string,
  statusData: {
    orderId: string;
    cartId: string;
    status: string;
  },
) => {
  const response = await axiosInstance.put(
    `/order/status/item/${itemId}`,
    statusData,
  );
  return response.data;
};

// In your API file
export const fetchAllCategories = async (): Promise<Category[]> => {
  const response = await axiosInstance.get<{ categories: Category[] }>(
    "/getAllCategories",
  );
  return response.data.categories; // Directly return the categories array
};

export const fetchAllActiveCategories = async (): Promise<Category[]> => {
  const response = await axiosInstance.get<{ categories: Category[] }>(
    "/getActiveCategories",
  );
  return response.data.categories; // Directly return the categories array
};

// In your api/apis.ts
export const addCategory = async (categoryData: CategoryInput | FormData) => {
  // Determine if we're sending JSON or FormData
  const isFormData = categoryData instanceof FormData;

  const config = isFormData
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : {};

  const response = await axiosInstance.post(
    "/addCategory",
    categoryData,
    config,
  );
  return response.data.category;
};

export const updateCategory = async (
  id: string,
  categoryData: FormData | CategoryInput,
) => {
  const isFormData = categoryData instanceof FormData;

  const config = isFormData
    ? { headers: { "Content-Type": "multipart/form-data" } }
    : { headers: { "Content-Type": "application/json" } };

  // For FormData, send as-is. For JSON, wrap in { category: ... } to match backend expectation
  const data = isFormData ? categoryData : { category: categoryData };

  const response = await axiosInstance.put(`/${id}`, data, config);
  return response.data.category;
};

export const deleteCategory = async (id: string) => {
  const response = await axiosInstance.delete(`/delete/${id}`);
  return response.data;
};

export const getAllContacts = async (): Promise<Contact[]> => {
  const response = await axiosInstance.get("/get/getAllContacts");
  return response.data.contacts;
};

// Get contact by ID
export const getContactById = async (id: string): Promise<Contact> => {
  const response = await axiosInstance.get(`/getContactById/${id}`);
  return response.data.contact;
};

// Delete contact by ID
export const deleteContact = async (
  id: string,
): Promise<{ success: boolean; message: string }> => {
  const response = await axiosInstance.delete(`/deleteContact/${id}`);
  return response.data;
};

export const getGoldPriceByKarat = async (
  karat: string,
): Promise<GoldPriceResponse> => {
  try {
    // Validate the karat parameter on the frontend as well for better UX
    if (!["18K", "22K", "14K", "24K"].includes(karat)) {
      throw new Error("Invalid karat value. Please use 18K, 22K, or 14K.");
    }

    const response = await axiosInstance.get(
      `/products/GET/get-gold-price/${karat}`,
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching gold price for ${karat}:`, error);
    throw error; // Re-throw the error to handle it in the component
  }
};

export const addTaxes = async (data: {
  type: string;
  rate: number;
}): Promise<Tax> => {
  const response = await axiosInstance.post("/tax/addTaxes", data);
  return response.data.taxes;
};

export const getTaxes = async (): Promise<Tax[]> => {
  const response = await axiosInstance.get("/tax/getTaxes");
  return response.data.taxes;
};

export const updateTax = async (
  data: {
    type: string;
    rate: number;
  },
  id: string,
): Promise<Tax> => {
  console.log(id);
  const response = await axiosInstance.put(`/tax/updateTax/${id}`, data);
  return response.data.taxes;
};

export const deleteTaxes = async (id: string): Promise<Tax> => {
  const response = await axiosInstance.delete(`/tax/deleteTax/${id}`);
  return response.data.taxes;
};

export const fetchCoupons = async () => {
  const response = await axiosInstance.get("/coupon/getCoupon");
  return response.data.coupons;
};

export const createCoupon = async (payload: CreateCouponPayload | FormData) => {
  const isFormData = payload instanceof FormData;

  const response = await axiosInstance.post("/coupon/create", payload, {
    headers: {
      "Content-Type": isFormData ? "multipart/form-data" : "application/json",
    },
  });

  return response.data;
};

export const updateCoupon = async (
  id: string,
  payload: UpdateCouponPayload | FormData,
) => {
  const isFormData = payload instanceof FormData;

  const response = await axiosInstance.put(
    `/coupon/updateCoupon/${id}`,
    payload,
    {
      headers: {
        "Content-Type": isFormData ? "multipart/form-data" : "application/json",
      },
    },
  );

  return response.data;
};

export const deleteCoupon = async (id: string) => {
  const response = await axiosInstance.delete(`/coupon/deleteCoupon/${id}`);
  return response.data;
};

export const getAllReviews = async () => {
  const response = await axiosInstance.get("/review/getAllReviews");
  return response.data;
};

export const updateReviewStatus = async (id: string, status: string) => {
  const response = await axiosInstance.put(`/review/admin/approve/${id}`, {
    status,
  });
  return response.data;
};

// src/api/invoice.ts

export const sendInvoiceEmail = async ({
  invoiceData,
  to,
  subject,
  message,
}: {
  invoiceData: any;
  to: string;
  subject: string;
  message: string;
}) => {
  const response = await axiosInstance.post("/send-invoice", {
    invoiceData,
    to,
    subject,
    message,
  });

  return response.data;
};

export const generateInvoicePDF = async (invoiceData: any) => {
  const response = await axiosInstance.post(
    "/generate-pdf",
    { invoiceData },
    {
      responseType: "blob", // Important for receiving binary PDF
    },
  );

  // Create a blob and trigger download
  const blob = new Blob([response.data], { type: "application/pdf" });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `invoice-${invoiceData.invoiceNumber}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();

  window.URL.revokeObjectURL(url);
};

export const getAllInvoices = async () => {
  const response = await axiosInstance.get("/invoice/getall");
  return response.data;
};

export const getInvoiceByNumber = async (invoiceNumber: string) => {
  const encodedInvoiceNumber = encodeURIComponent(invoiceNumber); // Encode '#' properly
  const response = await axiosInstance.get(
    `/getInvoice/${encodedInvoiceNumber}`,
  );
  return response.data;
};

export const saveInvoice = async (invoiceData: any) => {
  console.log("Invocie Data", invoiceData);
  const response = await axiosInstance.post("/save-invoice", invoiceData);
  return response.data;
};

export const updateOrderStatusById = async (
  orderId: string,
  status: string,
) => {
  const response = await axiosInstance.put(`order/${orderId}/status`, {
    status,
  });
  return response.data;
};

export const updateProductFlags = async (
  productId: string,
  flags: { isFeatured?: boolean; isNewArrival?: boolean },
) => {
  const response = await axiosInstance.put(
    `/products/${productId}/flags`,
    flags,
  );
  return response.data;
};

export const fetchFullInventoryReport = async () => {
  const response = await axiosInstance.get(
    "/products/getInventoryReport/report",
  );
  console.log("Inventory", response.data);
  return response.data;
};

// export const restockProduct = async (productId: string, quantity: number, sku: string) => {
//   const response = await axiosInstance.post(
//     "/products/restockProduct/restock",
//     {
//       productId,
//       quantity,
//       sku,
//     }
//   );
//   return response.data; // contains: success, message, inventory, updatedProduct
// };
// export const decreaseStockProduct = async (
//   productId: string,
//   quantity: number
// ) => {
//   const response = await axiosInstance.post(
//     "/products/decreaseStockProduct/restock",
//     {
//       productId,
//       quantity,
//     }
//   );
//   return response.data; // contains: success, message, inventory, updatedProduct
// };

// Update the restockProduct function to include sku
export const restockProduct = async (
  productId: string,
  quantity: number,
  sku: string,
) => {
  const response = await axiosInstance.post(
    "/products/restockProduct/restock",
    {
      productId,
      quantity,
      sku,
    },
  );
  return response.data;
};

// Create a similar function for deducting stock
export const deductStockProduct = async (
  productId: string,
  quantity: number,
  sku: string,
) => {
  const response = await axiosInstance.post(
    "/products/decreaseStockProduct/restock",
    {
      productId,
      quantity,
      sku,
    },
  );
  return response.data;
};

export const createAbout = async (formData: FormData) => {
  const response = await axiosInstance.post("/About/createAbout", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

// READ ONE (if only one exists)
export const getAllAbouts = async () => {
  const response = await axiosInstance.get("/About/getAllAbouts");
  return response.data;
};

// READ BY ID
export const getAboutById = async (id: string) => {
  const response = await axiosInstance.get(`/About/getAboutById/${id}`);
  return response.data;
};

// UPDATE
export const updateAbout = async (id: string, formData: FormData) => {
  const response = await axiosInstance.put(
    `/About/updateAbout/${id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// DELETE
export const deleteAbout = async (id: string) => {
  const response = await axiosInstance.delete(`/About/deleteAbout/${id}`);
  return response.data;
};

export const createBlog = async (formData: FormData) => {
  const response = await axiosInstance.post("/blog/create", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

// GET all blogs
export const getAllBlogs = async () => {
  const response = await axiosInstance.get("/blog/getAllBlogs");
  return response.data.data;
};
export const getAllNews = async () => {
  const response = await axiosInstance.get("/blog/getAllNews");
  return response.data.data;
};

// GET blog by ID
export const getBlogById = async (id: string) => {
  const response = await axiosInstance.get(`/blog/getBlogById/${id}`);
  return response.data.data;
};

// UPDATE blog by ID
export const updateBlog = async (id: string, formData: FormData) => {
  const response = await axiosInstance.put(`/blog/Update/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

// DELETE blog by ID
export const deleteBlog = async (id: string) => {
  const response = await axiosInstance.delete(`/blog/Delete/${id}`);
  return response.data;
};

export const dashboardData = async () => {
  const response = await axiosInstance.get("/admin/dashboard_details");
  return response.data;
};

export const createAttribute = async (payload: AttributePayload) => {
  // Construct the final data to send
  const data: AttributePayload = {
    label: payload.label,
    type: payload.type,
    isVariable: payload.isVariable,
    isRequired: payload.isRequired,
    isCharge: payload.isCharge,
  };

  // Only add options if type is 'options' and options array is valid
  if (payload.type === "options" && payload.options) {
    data.options = payload.options.filter((opt) => opt.trim() !== "");
  }

  const response = await axiosInstance.post("/attribute/createAttribute", data);
  return response.data;
};

export const updateAttribute = async (
  id: string,
  payload: AttributePayload,
) => {
  const data: AttributePayload = {
    label: payload.label,
    isRequired: payload.isRequired,
    isCharge: payload.isCharge,
    type: payload.type,
    isVariable: !!payload.isVariable,
    options:
      payload.type === "options"
        ? payload.options?.filter((opt) => opt.trim() !== "") || []
        : [],
  };

  const response = await axiosInstance.put(
    `/attribute/updateAttribute/${id}`,
    data,
  );
  return response.data;
};

export const getAllAttributes = async () => {
  const response = await axiosInstance.get("/attribute/getAllAttributes");
  return response.data;
};

export const createAttributeSet = async (payload: AttributeSetPayload) => {
  const response = await axiosInstance.post(
    "/attributeSet/attribute-sets",
    payload,
  );
  return response.data;
};

export const getAllAttributeSets = async () => {
  const response = await axiosInstance.get("/attributeSet/getAllAttributeSets");
  console.log(response.data.data);
  return response.data.data; // Extract the data array from the response
};

export const deleteAttribute = async (id: string) => {
  const response = await axiosInstance.delete(
    `/attribute/deleteAttribute/${id}`,
  );
  return response.data;
};

export const deleteAttributeSet = async (id: string) => {
  const response = await axiosInstance.delete(
    `/attributeSet/deleteAttributeSet/${id}`,
  );
  return response.data;
};

export const updateAttributeSet = async (
  id: string,
  data: { name: string; attributes: string[] },
) => {
  const response = await axiosInstance.put(
    `/attributeSet/updateAttributeSet/${id}`,
    data,
  );
  return response.data;
};

export const fetchBasePrice = async () => {
  const response = await axiosInstance.get("/base-price/getBasePrice");
  return response.data;
};
export const getAllKaratPrices = async () => {
  const response = await axiosInstance.get("/karat-prices/getAllKaratPrices");
  return response.data;
};

export const updateOrAddKaratPricePercentage = async (data: {
  karat: string;
  percentage: number;
}) => {
  const response = await axiosInstance.post("/karat-prices/", data);
  return response.data;
};

export const deleteKaratPrice = async (karat: string) => {
  const response = await axiosInstance.delete(`/karat-prices/${karat}`);
  return response.data;
};

export const saveOrUpdateBasePrice = async (price: number) => {
  const response = await axiosInstance.put("/products/update-price", {
    price,
  });
  return response.data;
};

export const getAttributeById = async (id: string): Promise<Attribute> => {
  const response = await axiosInstance.get<Attribute>(
    `/attribute/getAttributeById/${id}`,
  );
  return response.data;
};

export const getAttributeSetById = async (
  id: string,
): Promise<AttributeSet> => {
  const response = await axiosInstance.get(
    `/attributeSet/getAttributeSetById/${id}`,
  );
  return response.data;
};

// Add these functions to your existing API file

export const exportProductsToCSV = async () => {
  try {
    const response = await axiosInstance.get("/products-io/export", {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const importProductsFromCSV = async (
  file: File,
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
) => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await axiosInstance.post("/products-io/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const downloadTemplateCSV = async () => {
  try {
    const response = await axiosInstance.get("/products-io/template", {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    throw error;
  }
};
export const wishlist = async () => {
  try {
    const response = await axiosInstance.get(
      "/wishlist/getAllWishlistItems/counts",
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const fetchKeywordCounts = async (params?: {
  history?: boolean;
  export?: boolean;
  period?:
    | "daily"
    | "weekly"
    | "monthly"
    | "quarterly"
    | "halfyearly"
    | "yearly";
  startDate?: string;
  endDate?: string;
  keyword?: string;
  userSearch?: string;
  page?: number;
  limit?: number;
}) => {
  try {
    const queryParams = {
      history: params?.history ? "true" : undefined,
      export: params?.export ? "true" : undefined,
      period: params?.period,
      startDate: params?.startDate,
      endDate: params?.endDate,
      keyword: params?.keyword,
      userSearch: params?.userSearch,
      page: params?.page,
      limit: params?.limit,
    };

    (Object.keys(queryParams) as Array<keyof typeof queryParams>).forEach(
      (key) => queryParams[key] === undefined && delete queryParams[key],
    );

    if (params?.export) {
      const response = await axiosInstance.get("/search/admin/all", {
        params: queryParams,
        responseType: "blob",
      });
      return response;
    }

    // Normal JSON response
    const response = await axiosInstance.get("/search/admin/all", {
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getInventorySalesReport = async (
  params: InventorySalesReportParams,
): Promise<any> => {
  const response = await axiosInstance.get("/admin/reports/inventory-sales", {
    params,
    responseType: params.export ? "blob" : "json", // for file download if export=true
  });

  // If export = true, return the blob data for download
  if (params.export) {
    return {
      data: response.data,
      filename: response.headers["content-disposition"]?.split("filename=")[1],
    };
  }

  return response.data;
};

export const addToCart = async (
  payload: AddToCartPayload,
): Promise<AddToCartResponse> => {
  const response = await axiosInstance.post<AddToCartResponse>(
    "/cart/add",
    payload,
  );
  return response.data;
};

export const createBanner = async (formData: FormData) => {
  const response = await axiosInstance.post("/banner/createBanner", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

// 🔹 Get all banners
// export const getAllBanners = async () => {
//   const response = await axiosInstance.get("/banner/getAllBanners");
//   return response.data;
// };

interface Banner {
  _id: string;
  title: string;
  content: string;
  active: boolean;
  images: {
    mobile: string;
    desktop: string;
  };
  createdAt: string;
  updatedAt: string;
}

// 🔹 Get all banners
export const getAllBanners = async (): Promise<Banner[]> => {
  try {
    const response = await axiosInstance.get("/banner/getAllBanners");
    return response.data;
  } catch (error) {
    console.error("Error fetching banners:", error);
    throw error;
  }
};

// 🔹 Get a banner by ID
export const getBannerById = async (id: string) => {
  const response = await axiosInstance.get(`/banner/getBannerById/${id}`);
  return response.data;
};

// 🔹 Update an existing banner
export const updateBanner = async (id: string, formData: FormData) => {
  const response = await axiosInstance.put(
    `/banner/updateBanner/${id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// 🔹 Delete a banner by ID
export const deleteBanner = async (id: string) => {
  const response = await axiosInstance.delete(`/banner/deleteBanner/${id}`);
  return response.data;
};

// 🔹 Create a new influencer (video file = "file")
export const createInfluencer = async (formData: FormData) => {
  const response = await axiosInstance.post(
    "/influencers/createInfluencer",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// 🔹 Get all influencers
export const getAllInfluencers = async () => {
  const response = await axiosInstance.get("/influencers/getAllInfluencers");
  return response.data;
};

// 🔹 Get a single influencer by ID
export const getInfluencerById = async (id: string) => {
  const response = await axiosInstance.get(
    `/influencers/getInfluencerById/${id}`,
  );
  return response.data;
};

// 🔹 Update influencer (video file = "video")
export const updateInfluencer = async (id: string, formData: FormData) => {
  const response = await axiosInstance.put(
    `/influencers/updateInfluencer/${id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
  return response.data;
};

// 🔹 Delete an influencer
export const deleteInfluencer = async (id: string) => {
  const response = await axiosInstance.delete(
    `/influencers/deleteInfluencer/${id}`,
  );
  return response.data;
};

// 🔹 Create a new client review (with images)
export const createClientReview = async (formData: FormData) => {
  const response = await axiosInstance.post("/clientreviews/create", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

// 🔹 Get all client reviews
export const getAllClientReviews = async () => {
  const response = await axiosInstance.get("/clientreviews/getall");
  return response.data;
};

// 🔹 Get a client review by ID
export const getClientReviewById = async (id: string) => {
  const response = await axiosInstance.get(`/clientreviews/${id}`);
  return response.data;
};

// 🔹 Update a client review
export const updateClientReview = async (id: string, formData: FormData) => {
  const response = await axiosInstance.put(`/clientreviews/${id}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

// 🔹 Delete a client review
export const deleteClientReview = async (id: string) => {
  const response = await axiosInstance.delete(`/clientreviews/${id}`);
  return response.data;
};

export const getAllPinCodes = async (): Promise<pinCode[]> => {
  const response = await axiosInstance.get("/pinCode/pinCode");
  return response.data;
};

export const addPinCode = async (data: {
  pincode: string;
}): Promise<pinCode> => {
  const response = await axiosInstance.post("/pinCode/pinCode", data);
  return response.data;
};

export const updatePinCode = async (
  id: string,
  data: { pincode: string },
): Promise<pinCode> => {
  const response = await axiosInstance.put(`/pinCode/pinCode/${id}`, data);
  return response.data;
};

export const deletePinCode = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/pinCode/pinCode/${id}`);
};

export const getAllPagesAdmin = async (): Promise<PagesResponse> => {
  const response: ApiResponse<PagesResponse> =
    await axiosInstance.get("/pages/admin/all");
  return response.data;
};

// Create a new page

// Update these API functions in your api/apis file

export const createPage = async (data: any): Promise<PageResponse> => {
  const response: ApiResponse<PageResponse> = await axiosInstance.post(
    "/pages/create",
    data,
  );
  return response.data;
};

export const updatePage = async (
  id: string,
  data: any,
): Promise<PageResponse> => {
  const response: ApiResponse<PageResponse> = await axiosInstance.put(
    `/pages/${id}`,
    data,
  );
  return response.data;
};

// Delete a page
export const deletePage = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/pages/${id}`);
};

// Toggle page status
export const togglePageStatus = async (id: string): Promise<StatusResponse> => {
  const response: ApiResponse<StatusResponse> = await axiosInstance.patch(
    `/pages/${id}/status`,
  );
  return response.data;
};

export const createContactInfo = async (
  data: ContactInfo,
): Promise<ContactInfo> => {
  const response = await axiosInstance.post<ContactInfo>(
    "/contactinfo/createContact",
    data,
  );
  return response.data; // Directly return the contact info object
};
export const editContactInfo = async (
  id: string,
  data: ContactInfo,
): Promise<ContactInfo> => {
  const response = await axiosInstance.put<ContactInfo>(
    `/contactinfo/updateContact/${id}`,
    data,
  );
  return response.data;
};
export const setDefaultContactInfo = async (
  id: string,
): Promise<ContactInfo> => {
  const response = await axiosInstance.put<ContactInfo>(
    `/contactinfo/default/${id}`,
  );
  return response.data;
};

// Get contact info - handle both response formats
export const getContactInfo = async (): Promise<ContactInfo | null> => {
  try {
    const response = await axiosInstance.get<
      ContactInfo | { contactInfo: ContactInfo }
    >("/contactinfo/getAllContacts");

    // Handle both response formats
    if (response.data && typeof response.data === "object") {
      if ("contactInfo" in response.data) {
        return (response.data as { contactInfo: ContactInfo }).contactInfo;
      } else {
        return response.data as ContactInfo;
      }
    }

    return null;
  } catch (error: any) {
    // If it's a 404, return null (no data exists)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const getContactInfo1 = async (): Promise<ContactInfo | null> => {
  try {
    const response = await axiosInstance.get<
      ContactInfo | { contactInfo: ContactInfo }
    >("/contactinfo/get");

    // Handle both response formats
    if (response.data && typeof response.data === "object") {
      if ("contactInfo" in response.data) {
        return (response.data as { contactInfo: ContactInfo }).contactInfo;
      } else {
        return response.data as ContactInfo;
      }
    }

    return null;
  } catch (error: any) {
    // If it's a 404, return null (no data exists)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Delete contact info
export const deleteContactInfo = async (id: string): Promise<void> => {
  await axiosInstance.delete(`/contactinfo/deletebyid/${id}`);
};

export const cancelOrder = async (
  orderId: string,
  cancellationReason?: string,
): Promise<{
  success: boolean;
  message: string;
  order?: {
    _id: string;
    status: string;
    cancellationReason?: string;
  };
}> => {
  const response = await axiosInstance.put<{
    success: boolean;
    message: string;
    order?: {
      _id: string;
      status: string;
      cancellationReason?: string;
    };
  }>(`/order/cancel/${orderId}`, { cancellationReason });
  return response.data;
};

// Update the frontend API call to send all address fields
export const changeOrderAddress = async (
  orderId: string,
  addressData: {
    address: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    zipCode?: string;
    state?: string;
    country?: string;
    city?: string;
    addressId?: string;
  },
): Promise<{
  success: boolean;
  message: string;
  order?: Order;
}> => {
  const response = await axiosInstance.put<{
    success: boolean;
    message: string;
    order?: Order;
  }>(`/order/update-address/${orderId}`, addressData);
  return response.data;
};
export interface Customer {
  _id?: string;
  name: string;
  email: string;
  phone?: string;
}

export interface Address {
  street?: string;
  city: string;
  state?: string;
  country?: string;
  zipCode?: string;
}

export interface InvoiceItem {
  description: string;
  quantity: number;
  rate: number;
  amount: number;
}

export interface Invoice {
  _id: string;
  invoiceNumber: string;
  customer: Customer;
  date: string;
  dueDate?: string;
  items: InvoiceItem[];
  subtotal: number;
  tax?: number;
  discount?: number;
  finalAmount: number;
  paymentStatus: "Paid" | "Unpaid" | "Pending" | "Refund" | "Cancelled";
  shippingAddress?: Address;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InvoiceUpdateData {
  customer?: Partial<Customer>;
  date?: string;
  dueDate?: string;
  items?: InvoiceItem[];
  subtotal?: number;
  tax?: number;
  discount?: number;
  finalAmount?: number;
  paymentStatus?: Invoice["paymentStatus"];
  shippingAddress?: Partial<Address>;
  notes?: string;
}

export const updateInvoice = async (
  id: string,
  data: InvoiceUpdateData,
): Promise<ApiResponse<Invoice>> => {
  const response = await axiosInstance.put<ApiResponse<Invoice>>(
    `/invoice/update/${id}`,
    data,
  );
  return response.data;
};

export const deleteInvoice = async (id: string): Promise<ApiResponse<null>> => {
  const response = await axiosInstance.delete<ApiResponse<null>>(
    `/invoice/delete/${id}`,
  );
  return response.data;
};
